import React, { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMutation, useQuery } from '@apollo/client';
import { Button } from 'react-bootstrap';
import { moveDealToFolder } from '../../../../api/mutations';
import { getDealsForTeam } from '../../../../api/queries';
import { getDealFolders } from '../../../../api/queries_v2';
import { FolderTreeSelector } from '../../../../components/folder-tree-selector';
import PropdocsModal from '../../../../components/modal/PropdocsModal';
import { useNotifications } from '../../../../notifications';

interface MoveDealModalProps {
  isOpen: boolean;
  onClose: () => void;
  dealId: string;
  dealTitle: string;
  currentFolderId?: string;
  teamId: string;
  onSuccess: () => void;
}

export const MoveDealModal: React.FC<MoveDealModalProps> = ({
  isOpen,
  onClose,
  dealId,
  dealTitle,
  currentFolderId,
  teamId,
  onSuccess,
}) => {
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(currentFolderId ?? null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [, { success: notifySuccess, error: notifyError }] = useNotifications();

  // Reset selected folder when modal opens with different deal
  useEffect(() => {
    if (isOpen) {
      setSelectedFolderId(currentFolderId ?? null);
    }
  }, [isOpen, currentFolderId]);

  // Fetch all deal folders for the tree selector
  const { data: foldersData, loading: foldersLoading } = useQuery(getDealFolders as any, {
    variables: { teamId },
    skip: !isOpen,
  });

  const [moveDeal] = useMutation(moveDealToFolder, {
    onCompleted: () => {
      notifySuccess({ message: 'Deal moved successfully' });
      onSuccess();
      handleClose();
    },
    onError: (error) => {
      console.error('Error moving deal:', error);
      setIsSubmitting(false);
      notifyError({ message: error.message || 'Failed to move deal. Please try again.' });
    },
  });

  const handleClose = () => {
    setSelectedFolderId(currentFolderId ?? null);
    setIsSubmitting(false);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await moveDeal({
        variables: {
          dealId,
          folderId: selectedFolderId,
        },
        refetchQueries: [
          {
            query: getDealsForTeam,
            variables: { teamId, folderId: selectedFolderId },
          },
          {
            query: getDealsForTeam,
            variables: { teamId, folderId: currentFolderId ?? null },
          },
        ],
      });
    } catch (error) {
      // Error handling is done in the onError callback
      setIsSubmitting(false);
    }
  };

  return (
    <PropdocsModal
      show={isOpen}
      setShow={handleClose}
      size="md"
      disableClose={isSubmitting}
      title={<FormattedMessage id="deals.folders.move" defaultMessage="Move Deal to Folder" />}
    >
      <>
        <div>
          <div className="mb-3">
            <p>Move '{dealTitle}' to a different folder:</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="form-label">
                <FormattedMessage id="deals.folders.selectFolder" defaultMessage="Select Folder" />
              </label>
              {foldersLoading ? (
                <div className="text-muted small">
                  <FormattedMessage id="deals.folders.loading" defaultMessage="Loading folders..." />
                </div>
              ) : (
                <FolderTreeSelector
                  folders={foldersData?.dealFolders?.folders || []}
                  selectedFolderId={selectedFolderId}
                  onSelectionChange={setSelectedFolderId}
                  disabled={isSubmitting}
                  context="deals"
                />
              )}
            </div>
          </form>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1rem' }}>
          <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
            <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <FormattedMessage id="common.moving" defaultMessage="Moving..." />
            ) : (
              <FormattedMessage id="deals.folders.move" defaultMessage="Move Deal" />
            )}
          </Button>
        </div>
      </>
    </PropdocsModal>
  );
};

export default MoveDealModal;
